import {
	type ApplicationError,
	type DApp,
	type ForManagingDApps,
	type Template,
	type UseCase
} from 'core'
import { z } from 'zod'

import { HTTPClient, HTTPMethods } from './http_client'
import type { DAppDTO, TemplateDTO, UseCaseDTO } from './types'

export interface CreateDAppPayloadBaseDTO {
	description?: string
	name: string
	public_project_id: string
}

export interface CreateDAppFromTemplatePayloadDTO
	extends CreateDAppPayloadBaseDTO {
	template_id: string
}

export interface CreateDAppFromPromptPayloadDTO
	extends CreateDAppPayloadBaseDTO {
	from_user_prompt: {
		user_prompt: string
		use_case_ids: string[]
	}
}

export interface CreateDAppFromGithubPayloadDTO
	extends CreateDAppPayloadBaseDTO {
	from_github_source: {
		repo_url: string
		ref: string
	}
}

export class DAppsManager implements ForManagingDApps {
	readonly client = new HTTPClient()

	async getTemplates(): Promise<{
		data?: Template[]
		error?: ApplicationError
	}> {
		const { data, error } = await this.client.fetch<{
			templates: TemplateDTO[]
		}>(`/authv2/registry/api/v1/templates`)

		if (error) return { error: error }

		return {
			data: data?.templates.map(this.formatTemplateData) || []
		}
	}

	async getUseCases(): Promise<{
		data?: UseCase[]
		error?: ApplicationError
	}> {
		const { data, error } = await this.client.fetch<{
			use_cases: UseCaseDTO[]
		}>(`/authv2/registry/api/v1/usecases`)

		if (error) return { error: error }

		return {
			data: data!.use_cases.map(this.formatUseCaseData) || []
		}
	}

	async createDAppFromTemplate(
		payload: z.infer<typeof ForManagingDApps.CreateDAppFromTemplatePayload>
	): Promise<{ data?: DApp; error?: ApplicationError }> {
		const { error, data } = await this.client.fetch<
			DAppDTO,
			CreateDAppFromTemplatePayloadDTO
		>(`/authv2/projects/api/v1/dapps/templates`, {
			method: HTTPMethods.Post,
			data: {
				name: payload.name,
				public_project_id: payload.publicProjectId,
				template_id: payload.templateId,
				description: payload.description
			}
		})

		if (error) return { error: error }

		return {
			data: {
				id: data!.id,
				name: data!.name,
				version: data!.version,
				active: data!.active,
				projectId: data!.project_id,
				webIdeUrl: data!.web_ide_url,
				createdAt: data!.created_at,
				description: data!.description
			}
		}
	}

	async createDAppFromPrompt(
		payload: z.infer<typeof ForManagingDApps.CreateDAppFromPromptPayload>
	): Promise<{ data?: DApp; error?: ApplicationError }> {
		const payloadFormated: CreateDAppFromPromptPayloadDTO = {
			description: payload.description,
			name: payload.name,
			public_project_id: payload.publicProjectId,
			from_user_prompt: {
				use_case_ids: payload.useCaseIds,
				user_prompt: payload.userPrompt
			}
		}

		const { error, data } = await this.client.fetch<
			DAppDTO,
			CreateDAppFromPromptPayloadDTO
		>(`/authv2/projects/api/v1/dapps/templates`, {
			method: HTTPMethods.Post,
			data: payloadFormated
		})

		if (error) return { error: error }

		const dApp: DApp = {
			id: data!.id,
			name: data!.name,
			version: data!.version,
			active: data!.active,
			projectId: data!.project_id,
			webIdeUrl: data!.web_ide_url,
			createdAt: data!.created_at,
			description: data!.description
		}

		return {
			data: dApp
		}
	}

	async createDAppFromGithub(
		payload: Required<
			z.infer<typeof ForManagingDApps.CreateDAppFromGithubPayload>
		>
	): Promise<{ data?: DApp; error?: ApplicationError }> {
		const { error, data } = await this.client.fetch<
			DAppDTO,
			CreateDAppFromGithubPayloadDTO
		>(`/authv2/projects/api/v1/dapps/templates`, {
			method: HTTPMethods.Post,
			data: {
				name: payload.name,
				description: payload.description,
				from_github_source: {
					repo_url: payload.githubUrl,
					ref: payload.githubRepoLatestCommit
				},
				public_project_id: payload.publicProjectId
			}
		})

		if (error) return { error: error }

		return {
			data: {
				id: data!.id,
				name: data!.name,
				version: data!.version,
				active: data!.active,
				projectId: data!.project_id,
				webIdeUrl: data!.web_ide_url,
				createdAt: data!.created_at,
				description: data!.description
			}
		}
	}

	async openWorkspace(dappId: string): Promise<{ error?: ApplicationError }> {
		const { error } = await this.client.fetch(
			`/authv2/projects/api/v1/smart_contracts/${dappId}/web_ide`
		)
		return { error: error }
	}

	async isWorkspaceHealthy(
		workspaceUrl: string
	): Promise<{ data?: boolean; error?: ApplicationError }> {
		const { error } = await this.client.fetch(`${workspaceUrl}/ready`)
		return {
			data: !error
		}
	}

	private formatTemplateData(template: TemplateDTO): Template {
		return {
			createdAt: template.created_at,
			description: template.description,
			downloadCount: template.download_count,
			id: template.id,
			name: template.name,
			subtitle: template.sub_title,
			updatedAt: template.updated_at,
			version: template.version
		}
	}

	private formatUseCaseData(template: UseCaseDTO): UseCase {
		return {
			id: template.id,
			name: template.name,
			version: template.version,
			subtitle: template.sub_title,
			description: template.description,
			content: template.content,
			createdAt: template.created_at,
			updatedAt: template.updated_at
		}
	}
}

export class DAppsManagerMock implements ForManagingDApps {
	async getTemplates(): Promise<{
		data?: Template[]
		error?: ApplicationError
	}> {
		return Promise.resolve({
			data: [
				{
					createdAt: 'template.created_at',
					description: 'template.description',
					downloadCount: 10,
					id: 'template.id',
					name: 'template.name',
					subtitle: 'template.sub_title',
					updatedAt: 'template.updated_at',
					version: 'template.version'
				}
			]
		})
	}

	async getUseCases(): Promise<{
		data?: UseCase[]
		error?: ApplicationError
	}> {
		return Promise.resolve({
			data: [
				{
					id: '019206b6-b246-7a4a-9440-576cdbb06498',
					name: 'counter',
					version: '0.0.1',
					subtitle:
						'Simple operations for incrementing and decrementing a counter.',
					description:
						'This CIDL handles basic counter operations like incrementing and decrementing.',
					content: 'cidl: 0.9',
					createdAt: '2024-09-18T20:13:56.510295Z',
					updatedAt: '2024-09-18T20:13:56.510295Z'
				}
			]
		})
	}

	async createDAppFromTemplate(
		payload: z.infer<typeof ForManagingDApps.CreateDAppFromTemplatePayload>
	): Promise<{
		data?: DApp
		error?: ApplicationError
	}> {
		return Promise.resolve({
			data: {
				active: true,
				createdAt: '2024-05-28T12:34:56Z',
				description: payload.description,
				id: 'abcd1234-5678-efgh-9101-ijklmnopqrst',
				name: payload.name,
				projectId: payload.publicProjectId,
				version: '1.0.0',
				webIdeUrl: ''
			}
		})
	}

	async createDAppFromPrompt(
		payload: z.infer<typeof ForManagingDApps.CreateDAppFromPromptPayload>
	): Promise<{
		data?: DApp
		error?: ApplicationError
	}> {
		return Promise.resolve({
			data: {
				active: true,
				createdAt: '2024-05-28T12:34:56Z',
				description: payload.description,
				id: 'abcd1234-5678-efgh-9101-ijklmnopqrst',
				name: payload.name,
				projectId: payload.publicProjectId,
				version: '1.0.0',
				webIdeUrl: ''
			}
		})
	}

	async createDAppFromGithub(
		payload: z.infer<typeof ForManagingDApps.CreateDAppFromGithubPayload>
	): Promise<{
		data?: DApp
		error?: ApplicationError
	}> {
		return Promise.resolve({
			data: {
				active: true,
				createdAt: '2024-05-28T12:34:56Z',
				id: 'abcd1234-5678-efgh-9101-ijklmnopqrst',
				name: payload.name,
				projectId: payload.publicProjectId,
				webIdeUrl: '',
				version: '1.0.0',
				description: payload.description
			}
		})
	}

	openWorkspace(): Promise<{
		data?: string
		error?: ApplicationError
	}> {
		return Promise.resolve({
			data: ''
		})
	}

	isWorkspaceHealthy(): Promise<{ data?: boolean; error?: ApplicationError }> {
		return Promise.resolve({
			data: true
		})
	}
}
